<script setup>
// Lead Workspace — Overview tab. Every section reads straight off the
// already-loaded lead (Nexceo::LeadsController#show) or an existing,
// already-authorized endpoint: property matches (Nexceo::ProjectMatchingService
// via PropertyMatchesController), recent activity (Nexceo::LeadActivity), and
// tags (Chatwoot's own conversation labels, when the lead has a linked
// conversation — see docs/mywork tags decision). Nothing here is fabricated:
// expected value / lead score / match points all come from real stored/
// computed values, and any reference field with no backing data (decision
// maker, amenities preference, possession date) is simply omitted.
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import leadsAPI from 'dashboard/api/nexceo/leads';
import ConversationApi from 'dashboard/api/conversations';
import {
  formatBudgetRange,
  formatDateTime,
  formatMoney,
  humanize,
  timeAgo,
} from '../leadFormatters';

const props = defineProps({
  lead: { type: Object, required: true },
});
const emit = defineEmits(['reload']);

const router = useRouter();
const { accountId } = useAccount();

const matches = ref([]);
const isLoadingMatches = ref(true);
const activities = ref([]);
const isLoadingActivities = ref(true);
const tags = ref([]);
const newTag = ref('');
const isSavingTags = ref(false);
const isClearingFollowUp = ref(false);

async function loadMatches() {
  isLoadingMatches.value = true;
  try {
    const { data } = await leadsAPI.getPropertyMatches(props.lead.id, 3);
    matches.value = data?.matches ?? [];
  } catch {
    matches.value = [];
  } finally {
    isLoadingMatches.value = false;
  }
}

async function loadActivities() {
  isLoadingActivities.value = true;
  try {
    const { data } = await leadsAPI.getActivities(props.lead.id);
    activities.value = (data ?? []).slice(0, 6);
  } catch {
    activities.value = [];
  } finally {
    isLoadingActivities.value = false;
  }
}

async function loadTags() {
  if (!props.lead.conversation_id) {
    tags.value = [];
    return;
  }
  try {
    const { data } = await ConversationApi.getLabels(props.lead.conversation_id);
    tags.value = data?.payload ?? [];
  } catch {
    tags.value = [];
  }
}

async function addTag() {
  const label = newTag.value.trim().toLowerCase();
  if (!label || !props.lead.conversation_id) return;
  isSavingTags.value = true;
  try {
    const updated = Array.from(new Set([...tags.value, label]));
    await ConversationApi.updateLabels(props.lead.conversation_id, updated);
    tags.value = updated;
    newTag.value = '';
  } finally {
    isSavingTags.value = false;
  }
}

async function removeTag(label) {
  if (!props.lead.conversation_id) return;
  const updated = tags.value.filter(t => t !== label);
  await ConversationApi.updateLabels(props.lead.conversation_id, updated);
  tags.value = updated;
}

onMounted(() => {
  loadMatches();
  loadActivities();
  loadTags();
});

const requirementFields = computed(() => {
  const l = props.lead;
  const fields = [];
  if (l.property_type) fields.push({ icon: 'i-lucide-home', label: 'Property Type', value: l.property_type });
  const budget = formatBudgetRange(l);
  if (budget) fields.push({ icon: 'i-lucide-wallet', label: 'Budget', value: budget });
  if (l.preferred_location) fields.push({ icon: 'i-lucide-map-pin', label: 'Location', value: l.preferred_location });
  if (l.bedrooms) fields.push({ icon: 'i-lucide-layout-grid', label: 'Configuration', value: l.bedrooms });
  if (l.purpose) fields.push({ icon: 'i-lucide-target', label: 'Purpose', value: humanize(l.purpose) });
  if (l.timeline) fields.push({ icon: 'i-lucide-clock', label: 'Timeline', value: humanize(l.timeline) });
  return fields;
});

function openWorkspaceTab(tab) {
  router.push({
    name: 'nexceo_lead_workspace',
    params: { accountId: accountId.value, id: props.lead.id, tab },
  });
}

const isFollowUpOverdue = computed(
  () => !!props.lead.next_follow_up_at && new Date(props.lead.next_follow_up_at) < new Date()
);

async function markFollowUpDone() {
  isClearingFollowUp.value = true;
  try {
    await leadsAPI.update(props.lead.id, { next_follow_up_at: null });
    useAlert('Follow-up marked done.');
    emit('reload');
  } catch {
    useAlert('Failed to update follow-up.');
  } finally {
    isClearingFollowUp.value = false;
  }
}

const activitiesByDay = computed(() => {
  const groups = [];
  activities.value.forEach(a => {
    const day = new Date(a.created_at).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    });
    let group = groups.find(g => g.day === day);
    if (!group) {
      group = { day, items: [] };
      groups.push(group);
    }
    group.items.push(a);
  });
  return groups;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid grid-cols-1 xl:grid-cols-[1fr_1fr_320px] gap-4 p-4">
    <!-- Left: Lead Information + Tags -->
    <div class="flex flex-col gap-4">
      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-n-slate-12">Lead Information</h3>
        </div>
        <dl class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt class="text-xs text-n-slate-8">Name</dt>
            <dd class="text-n-slate-12">{{ lead.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Phone</dt>
            <dd class="text-n-slate-12">{{ lead.phone_number || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Email</dt>
            <dd class="text-n-slate-12 truncate">{{ lead.email || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Source</dt>
            <dd class="text-n-slate-12 capitalize">{{ lead.source_channel || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Project Interest</dt>
            <dd class="text-n-slate-12">{{ lead.project?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Budget</dt>
            <dd class="text-n-slate-12">{{ formatBudgetRange(lead) || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Configuration</dt>
            <dd class="text-n-slate-12">{{ lead.bedrooms || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Location Preference</dt>
            <dd class="text-n-slate-12">{{ lead.preferred_location || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Buying Purpose</dt>
            <dd class="text-n-slate-12 capitalize">{{ humanize(lead.purpose) || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-8">Assigned Agent</dt>
            <dd class="text-n-slate-12">{{ lead.assigned_agent?.name || 'Unassigned' }}</dd>
          </div>
        </dl>
        <div v-if="lead.requirement_notes" class="mt-3 pt-3 border-t border-n-weak">
          <p class="text-xs text-n-slate-8 mb-1">Notes</p>
          <p class="text-sm text-n-slate-12">{{ lead.requirement_notes }}</p>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Tags</h3>
        <div v-if="!lead.conversation_id" class="text-xs text-n-slate-8">
          Tags require a linked conversation.
        </div>
        <template v-else>
          <div class="flex flex-wrap gap-1.5 mb-2">
            <span
              v-for="tag in tags"
              :key="tag"
              class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400"
            >
              {{ tag }}
              <button class="hover:text-red-600" @click="removeTag(tag)">
                <span class="i-lucide-x size-3" />
              </button>
            </span>
            <span v-if="!tags.length" class="text-xs text-n-slate-8">No tags yet.</span>
          </div>
          <div class="flex items-center gap-1.5">
            <input
              v-model="newTag"
              type="text"
              placeholder="Add tag…"
              class="!border !border-solid !border-n-weak rounded-lg px-2 py-1 text-xs flex-1 bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
              @keyup.enter="addTag"
            />
            <button
              :disabled="isSavingTags || !newTag.trim()"
              class="text-xs px-2 py-1 rounded-lg bg-woot-500 text-white disabled:opacity-50"
              @click="addTag"
            >
              Add
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Middle: Customer Requirement + Matched Properties -->
    <div class="rounded-xl border border-n-weak bg-n-background p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-n-slate-12">Customer Requirement</h3>
        <button
          class="text-xs text-woot-600 hover:underline"
          @click="openWorkspaceTab('requirements')"
        >
          Edit
        </button>
      </div>
      <div v-if="requirementFields.length" class="grid grid-cols-2 gap-3">
        <div
          v-for="f in requirementFields"
          :key="f.label"
          class="flex items-start gap-2 rounded-lg bg-n-alpha-1 p-2"
        >
          <span :class="f.icon" class="size-4 text-woot-500 shrink-0 mt-0.5" />
          <div class="min-w-0">
            <p class="text-[10px] uppercase tracking-wide text-n-slate-8">{{ f.label }}</p>
            <p class="text-sm text-n-slate-12 truncate capitalize">{{ f.value }}</p>
          </div>
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-8">No requirement details captured yet.</p>

      <!-- Matched Properties -->
      <div class="mt-4 pt-3 border-t border-n-weak">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs font-semibold text-n-slate-11">
            Matched Properties<template v-if="matches.length"> ({{ matches.length }})</template>
          </p>
          <button
            class="text-xs text-woot-600 hover:underline"
            @click="openWorkspaceTab('property-match')"
          >
            View All
          </button>
        </div>
        <div v-if="isLoadingMatches" class="text-xs text-n-slate-8">Loading…</div>
        <div v-else-if="!matches.length" class="text-xs text-n-slate-8">
          No matching properties yet.
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="m in matches"
            :key="m.project_id"
            class="flex items-center gap-2 rounded-lg border border-n-weak p-2"
          >
            <div class="size-10 rounded-md bg-n-alpha-2 overflow-hidden shrink-0">
              <img
                v-if="m.hero_image_url"
                :src="m.hero_image_url"
                class="w-full h-full object-cover"
                alt=""
              />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-n-slate-12 truncate">{{ m.name }}</p>
              <p class="text-xs text-n-slate-8 truncate">
                {{ m.city || m.location || '—' }}
                <template v-if="m.price_min || m.price_max">
                  · {{ formatMoney(m.price_min, m.currency) }}–{{ formatMoney(m.price_max, m.currency) }}
                </template>
              </p>
            </div>
            <span
              class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400 shrink-0"
            >
              {{ m.score }} pts
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right: Next Follow-up, Recent Activity -->
    <div class="flex flex-col gap-4">
      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Next Follow-up</h3>
        <div v-if="lead.next_follow_up_at">
          <div
            class="flex items-center justify-between gap-2 rounded-lg p-2 mb-2"
            :class="isFollowUpOverdue ? 'bg-red-50 dark:bg-red-500/10' : 'bg-n-alpha-1'"
          >
            <p class="text-sm font-medium" :class="isFollowUpOverdue ? 'text-red-600' : 'text-n-slate-12'">
              {{ isFollowUpOverdue ? 'Call now — overdue' : formatDateTime(lead.next_follow_up_at) }}
            </p>
            <button
              :disabled="isClearingFollowUp"
              class="text-xs px-2 py-1 rounded-lg bg-woot-500 text-white shrink-0 disabled:opacity-50"
              @click="markFollowUpDone"
            >
              Mark Done
            </button>
          </div>
          <p class="text-xs text-n-slate-8">
            {{ formatDateTime(lead.next_follow_up_at) }}
          </p>
          <p v-if="lead.follow_up_count" class="text-xs text-n-slate-8 mt-1">
            {{ lead.follow_up_count }} follow-up(s) so far
          </p>
        </div>
        <p v-else class="text-sm text-n-slate-8">No follow-up scheduled.</p>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-n-slate-12">Recent Activity</h3>
          <button
            class="text-xs text-woot-600 hover:underline"
            @click="openWorkspaceTab('activity')"
          >
            View All
          </button>
        </div>
        <div v-if="isLoadingActivities" class="text-xs text-n-slate-8">Loading…</div>
        <div v-else-if="!activities.length" class="text-xs text-n-slate-8">
          No activity recorded yet.
        </div>
        <div v-else class="space-y-3">
          <div v-for="group in activitiesByDay" :key="group.day">
            <p class="text-[10px] font-semibold text-n-slate-7 uppercase tracking-wide mb-1">
              {{ group.day }}
            </p>
            <ul class="space-y-2">
              <li v-for="a in group.items" :key="a.id" class="text-xs">
                <p class="text-n-slate-12 font-medium">{{ a.friendly_title || humanize(a.activity_type) }}</p>
                <p class="text-n-slate-8">{{ timeAgo(a.created_at) }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
